import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionContext";
import { Plane, Package, LogOut, ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ModuleSelectPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { hasPermission, isMasterAdmin } = usePermissions();

  const canCargo = isMasterAdmin || hasPermission("cargo", "view");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col" data-testid="module-select-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e293b]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#d4af37] flex items-center justify-center">
            <span className="text-[#0f172a] font-bold">B</span>
          </div>
          <div className="leading-tight">
            <p className="text-white font-semibold text-sm">BSCA Operations</p>
            <p className="text-gray-400 text-xs">Charleroi South Brussels Airport</p>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="text-gray-400 hover:text-white hover:bg-white/10"
          data-testid="module-logout-btn"
        >
          <LogOut size={18} className="mr-2" /> Logout
        </Button>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <div className="text-center mb-10">
          <p className="text-gray-400 text-sm uppercase tracking-widest mb-2">
            Welcome{user?.full_name ? `, ${user.full_name}` : ""}
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white">Choose a module</h1>
          <p className="text-gray-400 text-sm mt-2">
            Select the department you want to work in. You can switch anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl">
          {/* Business Aviation */}
          <button
            onClick={() => navigate("/dashboard")}
            className="group relative overflow-hidden rounded-2xl border border-[#1e293b] bg-[#111c33] p-8 text-left transition-all hover:border-[#d4af37] hover:-translate-y-1"
            data-testid="module-business-aviation-btn"
          >
            <div className="w-14 h-14 rounded-xl bg-[#d4af37]/15 flex items-center justify-center mb-6">
              <Plane size={28} className="text-[#d4af37]" />
            </div>
            <h2 className="text-white text-xl font-bold mb-1">Business Aviation</h2>
            <p className="text-gray-400 text-sm mb-6">
              Flight management, planning, apron & operations.
            </p>
            <span className="inline-flex items-center gap-2 text-[#d4af37] text-sm font-semibold">
              Enter module <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </span>
          </button>

          {/* Cargo */}
          {canCargo ? (
            <button
              onClick={() => navigate("/cargo/dashboard")}
              className="group relative overflow-hidden rounded-2xl border border-[#1e293b] bg-[#111c33] p-8 text-left transition-all hover:border-emerald-400 hover:-translate-y-1"
              data-testid="module-cargo-btn"
            >
              <div className="w-14 h-14 rounded-xl bg-emerald-400/15 flex items-center justify-center mb-6">
                <Package size={28} className="text-emerald-400" />
              </div>
              <h2 className="text-white text-xl font-bold mb-1">Cargo</h2>
              <p className="text-gray-400 text-sm mb-6">
                Import / export cargo, planning, services & agencies.
              </p>
              <span className="inline-flex items-center gap-2 text-emerald-400 text-sm font-semibold">
                Enter module <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          ) : (
            <div
              className="relative overflow-hidden rounded-2xl border border-[#1e293b] bg-[#0c1526] p-8 text-left opacity-60 cursor-not-allowed"
              data-testid="module-cargo-locked"
              title="You don't have access to the Cargo module yet"
            >
              <div className="w-14 h-14 rounded-xl bg-gray-700/40 flex items-center justify-center mb-6">
                <Package size={28} className="text-gray-500" />
              </div>
              <h2 className="text-gray-300 text-xl font-bold mb-1 flex items-center gap-2">
                Cargo <Lock size={16} className="text-gray-500" />
              </h2>
              <p className="text-gray-500 text-sm">
                In development — access is granted by the Master Admin.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
